import * as Guards from './DataAnalysisYield.type.type.guard'
import { Moment } from 'moment'
import { WithError } from '../../models/data-science/with-error.model'
import { HFType } from '../../../../shared/constants/hf-type.enum'

interface DataAnalysisYieldAbstract<T> {
  id: number
  analysisParams: string | null
  businessUnit: string
  contractNb: string
  dataAnalysisComment: DataAnalysisComment | null
  depthMeter: number
  grabDepthMeter: number
  durationSeconds: number
  foundationId: string // Used as the primary key by DWall
  foundationName: string
  foundationType: HFType
  grippersRatio: number
  guideWallLevel: number
  jobsiteId: string
  jobsiteName: string
  lastDtUpdate: Moment
  process: 'HF' | 'KS' | 'CFA' | 'PRECAST' | 'SMX' | 'HFKS'
  technique: 'PILING' | 'DWALL'
  yieldLinearMeterHour: number
  lengthMeter: number
  thicknessMeter: number
  hlinkGlobalSiteYield: T
  hlinkGlobalSiteYieldSDMD: string
  hlinkGlobalSiteYieldSDME: string
  hlinkGlobalSiteYieldSEMD: string
  hlinkGlobalSiteYieldSEME: string
  hlinkPlateauFilter: T
  hlinkPeakFilter: T
  hlinkGrabFilter: T
  hlinkBeforeFilters: T
  hlinkPeakFilterMarking: T
  hlinkPlateauFilterMarking: T
  hlinkKSDepth: T
  hlinkKSDrilledDepth: T
  hlinkKSYield: T
  hlinkPileYield: T
  hlinkPileYieldBis: T
  hlinkPileYieldSE: T
  hrefPileBottom: T
  hrefSpeedFilterDisabled: T
  hrefSpeedFilterEnabled: T
  dataAnalysisYieldSoilLayers: DataAnalysisYieldSoilLayer[]
  dataAnalysisYieldSlices: DataAnalysisYieldSlice[]
}

/** @see {isDataAnalysisYield} ts-auto-guard:type-guard */
export type DataAnalysisYield = DataAnalysisYieldAbstract<string>

/** @see {isDataAnalysisYieldWithError} ts-auto-guard:type-guard */
export type DataAnalysisYieldWithError = DataAnalysisYieldAbstract<
  WithError<string>
>

/** @see {isDataAnalysisYieldSoilLayer} ts-auto-guard:type-guard */
export interface DataAnalysisYieldSoilLayer {
  depthEnd?: number | null
  depthStart?: number | null
  durationSeconds?: number | null
  geologyDescription?: string | null
  geologyName?: string | null
  geologyProjectCode?: string | null
  grippersRatio?: number | null
  id?: number | null
  layerHeight?: number | null
  yieldLinearMeterHour?: number | null
  lastDtUpdate?: Moment | null
}

/** @see {isDataAnalysisYieldSlice} ts-auto-guard:type-guard */
export interface DataAnalysisYieldSlice {
  depthEnd?: number | null
  depthStart?: number | null
  drumDiameter?: number | null
  durationSeconds?: number | null
  geologyName?: string | null
  grippersRatio?: number | null
  id?: number | null
  lastDtUpdate?: Moment | null
  layerHeight?: number | null
  machine?: string | null
  operator?: string | null
  speedFilter?: boolean | null
  teeth?: string | null
  totalGrippActive?: number | null
  totalPoints?: number | null
  yieldCubicMeterHour?: number | null
  yieldLinearMeterHour?: number | null
  yieldSquareMeterHour?: number | null
}

/** @see {isDataAnalysisYieldSliceForTable} ts-auto-guard:type-guard */
export interface DataAnalysisYieldSliceForTable extends DataAnalysisYieldSlice {
  parentId: number
  empty: boolean
}

/** @see {isDataAnalysisYieldSoilLayerForTable} ts-auto-guard:type-guard */
export interface DataAnalysisYieldSoilLayerForTable extends DataAnalysisYieldSoilLayer {
  parentId: number
  empty: boolean
}

export function emptyDataAnalysisYieldSliceForTable(
  data: DataAnalysisYield,
): DataAnalysisYieldSliceForTable {
  return {
    parentId: data.id,
    empty: true,
  }
}

export function emptyDataAnalysisYieldSoilLayerForTable(
  data: DataAnalysisYield,
): DataAnalysisYieldSoilLayerForTable {
  return {
    parentId: data.id,
    empty: true,
  }
}

/** @see {isDataAnalysisYieldForTable} ts-auto-guard:type-guard */
export interface DataAnalysisYieldForTable {
  dataAnalysisYields: DataAnalysisYield[]
  dataAnalysisYieldSlices: DataAnalysisYieldSliceForTable[]
}

export type SiteYieldKey = 'SDMD' | 'SDME' | 'SEMD' | 'SEME'

export type SiteYield = Record<SiteYieldKey, WithError<string>>

export function loadingSiteYield(): SiteYield {
  const loading = new WithError<string>(null, true, null, 'Loading...')
  return { SDMD: loading, SDME: loading, SEMD: loading, SEME: loading }
}

export enum DataAnalysisComment {
  TROUBLE_NONE = 'TROUBLE_NONE',
  TROUBLE_MISSING_DATA = 'TROUBLE_MISSING_DATA',
  TROUBLE_HYDROGRAB = 'TROUBLE_HYDROGRAB',
  TROUBLE_GEOLOGY = 'TROUBLE_GEOLOGY',
  TROUBLE_GUIDEWALL = 'TROUBLE_GUIDEWALL',
  TROUBLE_GRIP = 'TROUBLE_GRIP',
  TROUBLE_HYGRB_GEO_GWALL_GRP = 'TROUBLE_HYGRB_GEO_GWALL_GRP',
  TROUBLE_HYGRB_GEO_GWALL = 'TROUBLE_HYGRB_GEO_GWALL',
  TROUBLE_HYGRB_GEO_GRP = 'TROUBLE_HYGRB_GEO_GRP',
  TROUBLE_HYGRB_GWALL_GRP = 'TROUBLE_HYGRB_GWALL_GRP',
  TROUBLE_HYGRB_GEO = 'TROUBLE_HYGRB_GEO',
  TROUBLE_HYGRB_GWALL = 'TROUBLE_HYGRB_GWALL',
  TROUBLE_HYGRB_GRP = 'TROUBLE_HYGRB_GRP',
  TROUBLE_GEO_GWALL_GRP = 'TROUBLE_GEO_GWALL_GRP',
  TROUBLE_GEO_GWALL = 'TROUBLE_GEO_GWALL',
  TROUBLE_GEO_GRP = 'TROUBLE_GEO_GRP',
  TROUBLE_GWALL_GRP = 'TROUBLE_GWALL_GRP',
}

export const DATA_ANALYSIS_YIELD_GROUP_BY_OPTIONS = [
  'foundationName',
  'machine',
  'operator',
  'teeth',
  'drumDiameter',
  'geologyName',
] as const

export type DataAnalysisYieldGroupByOption =
  (typeof DATA_ANALYSIS_YIELD_GROUP_BY_OPTIONS)[number]

export type FlatDataAnalysisYieldSlice = DataAnalysisYieldSlice & {
  foundationName: string
}

export type DataAnalysisYieldGroup<
  T = DataAnalysisYieldSlice | DataAnalysisYieldSoilLayer,
> = {
  foundationName?: string
  machine?: string
  operator?: string
  teeth?: string
  drumDiameter?: string
  geologyName?: string
  durationSeconds: number
  layerHeight: number
  yieldCubicMeterHour: number
  yieldLinearMeterHour: number
  yieldSquareMeterHour: number
  items: T[]
  hash: string
}
export type DataAnalysisYieldSliceGroup =
  DataAnalysisYieldGroup<DataAnalysisYieldSlice>

export { Guards }
